import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Badge } from "react-bootstrap";
import styled, { keyframes, css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { Link } from "react-router-dom";
import { 
  faBolt, 
  faFire, 
  faWaveSquare, 
  faStar, 
  faGem, 
  faMicroscope, 
  faRobot,
  faArrowRight,
  faShieldAlt,
  faHeart,
  faDownload,
  faCrown,
  faMedal,
  faRocket,
  faCircleCheck,
  faEye,
} from "@fortawesome/free-solid-svg-icons";

// Import images (update these paths to match your actual files)
import laserepi from "../Assets/series2.png";
import thermiqRF from "../Assets/rff.png";
import thermilifHIFU from "../Assets/thermdev.png";
import reviveIPL from "../Assets/revd.png";
import pictron11 from "../Assets/pback.png";
import axisoneCO2 from "../Assets/codevice.png";
import aiReveal from "../Assets/aiskinan.jpeg";

// Simplified Animations - removed heavy 3D transforms
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const gradientFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

// Removed heavy marquee animation
const marquee = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
`;

const letterIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const letterOut = keyframes`
  0% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
`;

// Your existing styled components with performance optimizations
const WordContainer = styled.span`
  display: inline-block;
  color: #8a63d2;
  position: relative;
  min-width: 120px;
  text-align: left;
  font-weight: 800;
  will-change: transform; /* Optimize for animations */
  
  @media (max-width: 576px) {
    min-width: 100px;
    font-size: 1.8rem;
  }
`;

const Letter = styled.span`
  display: inline-block;
  position: relative;
  animation: ${letterIn} 0.3s forwards;
  will-change: transform, opacity;

  &.hide {
    animation: ${letterOut} 0.3s forwards;
  }
`;

const HeroContainer = styled.div`
  position: relative;
  overflow-x: hidden; /* Changed from hidden to hidden to prevent horizontal scroll issues */
  overflow-y: visible;
  background: linear-gradient(135deg, #fafafa 0%, #f0f0ff 100%);
  width: 100%;
`;

const HeroBanner = styled.div`
  position: relative;
  width: 100%;
  height: 80vh;
  min-height: 600px;
  background: linear-gradient(135deg, rgba(21, 27, 84, 0.7), rgba(138, 99, 210, 0.7)), 
              url('https://img.freepik.com/premium-photo/beautiful-young-asian-woman-with-radiant-skin-confident-expression-studio-setting_729149-185667.jpg?uid=P37862907&ga=GA1.1.865323815.1697063865&w=740');
  background-size: cover;
  background-position: center;
  /* Removed background-attachment: fixed which causes heavy repaints */
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  margin-bottom: 4rem;
  clip-path: polygon(0 0, 100% 0, 100% 90%, 0 100%);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.3) 100%);
    pointer-events: none; /* Improve performance */
  }

  @media (max-width: 768px) {
    height: 70vh;
    min-height: 550px;
    clip-path: polygon(0 0, 100% 0, 100% 95%, 0 100%);
    margin-bottom: 2rem;
  }

  @media (max-width: 576px) {
    height: 60vh;
    min-height: 500px;
  }
`;

// Removed FloatingParticles completely for better performance

const BannerContent = styled.div`
  max-width: 900px;
  padding: 0 20px;
  z-index: 10;
  position: relative;
  width: 100%;
  
  @media (max-width: 576px) {
    padding: 0 15px;
  }
`;

const BannerBrand = styled.div`
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: 8px;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  color: rgba(255,255,255,0.9);
  animation: ${fadeIn} 1s ease-out 0.6s forwards;
  opacity: 0;
  position: relative;
  display: inline-block;
  
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 50px;
    height: 2px;
    background: linear-gradient(90deg, transparent, white, transparent);
  }
  
  &::before { left: -70px; }
  &::after { right: -70px; }
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    letter-spacing: 5px;
    
    &::before, &::after { 
      width: 30px; 
    }
    &::before { left: -40px; }
    &::after { right: -40px; }
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    letter-spacing: 3px;
    
    &::before, &::after { 
      width: 20px; 
    }
    &::before { left: -30px; }
    &::after { right: -30px; }
  }
`;

const BannerTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  background: linear-gradient(90deg, #ffffff, #ffd700, #8a63d2, #ffffff);
  background-size: 300% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${fadeIn} 1s ease-out, ${gradientFlow} 6s linear infinite;
  will-change: background-position;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
  
  @media (max-width: 576px) {
    font-size: 2.2rem;
    margin-bottom: 1rem;
  }
`;

const BannerSubtitle = styled.p`
  font-size: 1.8rem;
  margin-bottom: 2.5rem;
  animation: ${fadeIn} 1s ease-out 0.3s forwards;
  opacity: 0;
  font-weight: 300;
  letter-spacing: 2px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.1rem;
    line-height: 1.4;
    padding: 0 10px;
  }
`;

// Simplified Marquee - using CSS transform3d for hardware acceleration
const MarqueeContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(10px);
  padding: 12px 0;
  margin-top: 30px;
  border-radius: 50px;
  position: relative;
  transform: translateZ(0); /* Hardware acceleration */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 50px;
    height: 100%;
    background: linear-gradient(90deg, rgba(0,0,0,0.3), transparent);
    z-index: 2;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50px;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(0,0,0,0.3));
    z-index: 2;
    pointer-events: none;
  }
  
  @media (max-width: 576px) {
    padding: 8px 0;
    margin-top: 20px;
    border-radius: 30px;
  }
`;

const MarqueeContent = styled.div`
  display: flex;
  animation: ${marquee} 30s linear infinite;
  white-space: nowrap;
  transform: translateZ(0); /* Hardware acceleration */
  backface-visibility: hidden;
  perspective: 1000px;
  
  &:hover {
    animation-play-state: paused;
  }
  
  @media (max-width: 768px) {
    animation-duration: 40s;
  }
`;

const MarqueeItem = styled.span`
  display: inline-block;
  padding: 0 20px;
  font-size: 1.2rem;
  color: white;
  font-weight: 500;
  letter-spacing: 1px;
  
  i, svg {
    margin-right: 8px;
    color: #ffd700;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 15px;
  }
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
    padding: 0 10px;
  }
`;

const HeroImage = styled.div.attrs({
  style: props => ({
    animation: props.slideIn ? css`${slideInLeft} 0.8s ease-out` : css`${float} 8s ease-in-out infinite`
  })
})`
  position: relative;
  z-index: 1;
  padding: 20px;
  text-align: center;
  transform: translateZ(0); /* Hardware acceleration */
  backface-visibility: hidden;
  
  img {
    max-width: 100%;
    max-height: 600px;
    width: auto;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 20px 40px rgba(138, 99, 210, 0.4));
    transition: transform 0.3s ease;
    border-radius: 20px;
    will-change: transform;
    
    &:hover {
      transform: scale(1.05);
    }
  }

  @media (max-width: 992px) {
    margin-bottom: 2rem;
    
    img {
      max-height: 450px;
    }
  }
  
  @media (max-width: 768px) {
    padding: 10px;
    
    img {
      max-height: 350px;
    }
  }
  
  @media (max-width: 576px) {
    img {
      max-height: 280px;
    }
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  animation: ${css`${slideInRight} 0.8s ease-out`};
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 40px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transform: translateZ(0);
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    margin: 0 15px;
  }
  
  @media (max-width: 576px) {
    padding: 25px 15px;
    border-radius: 20px;
  }
`;

const HeroTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  color: #151B54;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
    text-align: center;
  }
`;

const HeroText = styled.p`
  font-size: 1.2rem;
  color: #444;
  margin-bottom: 2rem;
  line-height: 1.8;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    text-align: center;
  }
`;

const WhatsappButton = styled.a`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  box-shadow: 0 10px 30px rgba(18, 140, 126, 0.4);
  z-index: 999;
  transition: transform 0.3s ease;
  transform: translateZ(0);
  
  &:hover {
    transform: scale(1.1);
  }
  
  @media (max-width: 576px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
    bottom: 15px;
    right: 15px;
  }
`;

// Optimized Catalogue Section - removed heavy background animations
const CatalogueSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
  
  @media (max-width: 576px) {
    padding: 40px 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
  position: relative;
  z-index: 2;
  padding: 0 15px;
  
  @media (max-width: 576px) {
    margin-bottom: 30px;
  }
`;

const SectionTag = styled(Badge)`
  background: linear-gradient(135deg, #8a63d2, #4a90e2);
  color: white;
  padding: 8px 20px;
  border-radius: 40px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  font-size: 0.9rem;
  border: 2px solid rgba(255,255,255,0.3);
  
  @media (max-width: 576px) {
    padding: 6px 15px;
    font-size: 0.8rem;
    margin-bottom: 10px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 3.2rem;
  font-weight: 800;
  color: #151B54;
  margin-bottom: 15px;
  position: relative;
  display: inline-block;
  
  span {
    background: linear-gradient(135deg, #8a63d2, #4a90e2, #ff6b6b);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, transparent, #8a63d2, #4a90e2, #8a63d2, transparent);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
    
    &::after {
      width: 60px;
      height: 3px;
      bottom: -10px;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #666;
  max-width: 700px;
  margin: 25px auto 0;
  line-height: 1.7;
  padding: 0 15px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    line-height: 1.5;
    margin-top: 20px;
  }
`;

// Simplified Device Card - removed heavy transforms and animations
const DeviceCard = styled.div`
  background: white;
  border-radius: 30px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(138, 99, 210, 0.1);
  position: relative;
  transform: translateZ(0);
  backface-visibility: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(138, 99, 210, 0.2);
    border-color: #8a63d2;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, #8a63d2, #4a90e2, #8a63d2);
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    border-radius: 25px;
    margin-bottom: 25px;
    
    &:hover {
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 576px) {
    border-radius: 20px;
    margin-bottom: 20px;
    margin-left: 10px;
    margin-right: 10px;
    
    &::before {
      height: 4px;
    }
  }
`;

// Removed CardShine entirely

const DeviceRow = styled(Row)`
  align-items: center;
  padding: 30px;

  @media (max-width: 768px) {
    padding: 20px;
    flex-direction: column;
  }
  
  @media (max-width: 576px) {
    padding: 15px;
  }
`;

const DeviceImageCol = styled(Col)`
  text-align: center;
  padding: 15px;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 10px;
    margin-bottom: 15px;
  }
`;

// Simplified Image Wrapper - removed heavy filter effects
const DeviceImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  padding: 15px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const DeviceImage = styled.img`
  max-width: 100%;
  max-height: 320px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.1));
  position: relative;
  z-index: 2;
  transition: transform 0.3s ease;
  transform: translateZ(0);

  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 992px) {
    max-height: 280px;
  }
  
  @media (max-width: 768px) {
    max-height: 250px;
  }
  
  @media (max-width: 576px) {
    max-height: 200px;
  }
`;

// Simplified Badge - removed animation
const DeviceBadge = styled.div`
  position: absolute;
  top: 0px;
  right: 10px;
  background: linear-gradient(135deg, ${props => props.color}, ${props => props.color}CC);
  color: white;
  padding: 8px 18px;
  border-radius: 40px;
  font-size: 0.9rem;
  font-weight: 700;
  z-index: 10;
  box-shadow: 0 5px 10px ${props => props.color}40;
  border: 2px solid rgba(255,255,255,0.3);
  letter-spacing: 1px;
  text-transform: uppercase;

  i, svg {
    margin-right: 6px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 15px;
    font-size: 0.8rem;
    right: 5px;
    top: 5px;
  }
  
  @media (max-width: 576px) {
    padding: 4px 12px;
    font-size: 0.7rem;
    right: 0;
  }
`;

const DeviceContentCol = styled(Col)`
  padding: 20px 25px;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
    text-align: center;
  }
  
  @media (max-width: 576px) {
    padding: 5px 10px;
  }
`;

const DeviceCategory = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 20px;
  background: ${props => props.color}15;
  color: ${props => props.color};
  border-radius: 40px;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 15px;
  border: 1px solid ${props => props.color}30;
  text-transform: uppercase;
  letter-spacing: 1px;

  i, svg {
    margin-right: 8px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 768px) {
    padding: 6px 18px;
    font-size: 0.9rem;
  }
  
  @media (max-width: 576px) {
    padding: 5px 15px;
    font-size: 0.8rem;
    margin-bottom: 10px;
  }
`;

const DeviceName = styled.h3`
  font-size: 2.2rem;
  font-weight: 800;
  color: #151B54;
  margin-bottom: 15px;
  line-height: 1.2;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #8a63d2, transparent);
    border-radius: 2px;
  }
  
  @media (max-width: 992px) {
    font-size: 2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    
    &::after {
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
    }
  }
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
    
    &::after {
      width: 60px;
      height: 3px;
      bottom: -5px;
    }
  }
`;

const DeviceDescription = styled.p`
  color: #555;
  line-height: 1.7;
  margin-bottom: 20px;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
    text-align: center;
    padding: 0 10px;
  }
  
  @media (max-width: 576px) {
    font-size: 0.9rem;
    line-height: 1.4;
    padding: 0 5px;
  }
`;

const FeatureTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    justify-content: center;
    gap: 8px;
    margin-bottom: 20px;
  }
  
  @media (max-width: 576px) {
    gap: 6px;
    margin-bottom: 15px;
  }
`;

// Simplified Feature Tag - removed hover animation
const FeatureTag = styled.span`
  background: #f8f9ff;
  color: #333;
  padding: 8px 16px;
  border-radius: 40px;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${props => props.color}30;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.color}10;
  }

  i, svg {
    color: ${props => props.color};
    font-size: 0.9rem;
  }
  
  @media (max-width: 768px) {
    padding: 6px 14px;
    font-size: 0.85rem;
    gap: 6px;
  }
  
  @media (max-width: 576px) {
    padding: 4px 10px;
    font-size: 0.75rem;
    gap: 4px;
  }
`;

// Simplified Explore Button
const ExploreBtn = styled(Button)`
  background: linear-gradient(135deg, #8a63d2, #6a4aa2);
  border: none;
  color: white;
  padding: 12px 30px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 5px 15px rgba(138, 99, 210, 0.3);
  transform: translateZ(0);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(138, 99, 210, 0.4);
    background: linear-gradient(135deg, #9b74e6, #7b55b5);
  }

  i, svg {
    margin-left: 8px;
  }
  
  @media (max-width: 768px) {
    padding: 10px 25px;
    font-size: 0.95rem;
    margin: 0 auto;
    display: block;
    width: fit-content;
  }
  
  @media (max-width: 576px) {
    padding: 8px 20px;
    font-size: 0.9rem;
    width: 100%;
    max-width: 250px;
  }
`;

// Simplified Download Section
const DownloadSection = styled.section`
  padding: 60px 0;
  background: linear-gradient(135deg, #0a0a2a 0%, #1a1a3a 50%, #2a1a4a 100%);
  position: relative;
  overflow: hidden;
  margin-top: 30px;
  border-radius: 50px 50px 0 0;
  
  @media (max-width: 768px) {
    padding: 40px 0;
    border-radius: 30px 30px 0 0;
  }
  
  @media (max-width: 576px) {
    padding: 30px 0;
    margin-top: 20px;
  }
`;

const DownloadContainer = styled(Container)`
  position: relative;
  z-index: 2;
  padding: 0 15px;
`;

const DownloadCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  padding: 50px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  text-align: center;
  position: relative;
  margin: 0 15px;
  transform: translateZ(0);
  
  @media (max-width: 768px) {
    padding: 40px 25px;
    border-radius: 40px;
    margin: 0 10px;
  }
  
  @media (max-width: 576px) {
    padding: 30px 15px;
    border-radius: 30px;
    margin: 0;
  }
`;

const DownloadTitle = styled.h2`
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
  
  span {
    background: linear-gradient(135deg, #8a63d2, #4a90e2, #ff6b6b);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
    margin-bottom: 15px;
  }
`;

const DownloadText = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 25px;
  }
  
  @media (max-width: 576px) {
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 20px;
    padding: 0 5px;
  }
`;

const DownloadBtn = styled(Button)`
  background: linear-gradient(135deg, #8a63d2, #4a90e2);
  border: none;
  color: white;
  padding: 15px 40px;
  border-radius: 60px;
  font-weight: 700;
  font-size: 1.2rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 15px 30px rgba(138, 99, 210, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.3);
  transform: translateZ(0);

  &:hover {
    transform: scale(1.05) translateY(-3px);
    box-shadow: 0 20px 40px rgba(138, 99, 210, 0.5);
  }

  i, svg {
    margin-right: 10px;
  }
  
  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 1.1rem;
  }
  
  @media (max-width: 576px) {
    padding: 10px 25px;
    font-size: 1rem;
    width: 100%;
    max-width: 300px;
  }
`;

const StatsRow = styled(Row)`
  margin-top: 40px;
  position: relative;
  z-index: 2;
  
  @media (max-width: 576px) {
    margin-top: 30px;
    gap: 15px;
  }
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 5px;
  background: linear-gradient(135deg, #ffd700, #ffffff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.8rem;
  }
`;

// Main Component
function HeroSection() {
  const words = ["Safe", "Quick", "Painless", "Effective", "Advanced", "Precision"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [animationState, setAnimationState] = useState("show");
  const phoneNumber = "9647744052020";

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationState("hide");
      setTimeout(() => {
        setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
        setAnimationState("show");
      }, 300);
    }, 2000);
    return () => clearInterval(interval);
  }, [words.length]);

  // Device data from catalogue
  const devices = [
    {
      id: 1,
      name: "Laser Hair Removal",
      category: "Hair Removal",
      description: "Professional IPL device with dual handpieces and advanced air cooling (-35°C). Fast, safe, and effective for all skin types.",
      features: ["Dual Handpieces", "Air Cooling -35°C", "Rectangular Spot", "All Skin Types"],
      icon: faBolt,
      color: "#FF6B6B",
      image: laserepi,
      badge: "✨ NEW",
      route: "/HAIRREMOVAL"
    },
     {
      id: 2,
      name: "Revive IPL",
      category: "Multi-Purpose",
      description: "Advanced IPL system addressing over 20 skin concerns including acne, pigmentation, and hair removal with OPT technology.",
      features: ["20+ Treatments", "OPT Technology", "MSP Pulsing", "Advanced Cooling"],
      icon: faStar,
      color: "#50C878",
      image: reviveIPL || laserepi,
      badge: "⭐ VERSATILE",
      route: "/revive"
    },
    
    {
      id: 3,
      name: "Thermilif HIFU",
      category: "Skin Lifting",
      description: "World-first technology combining two powerful energy sources for exceptional skin lifting and shaping results.",
      features: ["HIFU Technology", "Dual Energy", "Skin Lifting", "Body Shaping"],
      icon: faWaveSquare,
      color: "#4A90E2",
      image: thermilifHIFU || laserepi,
      badge: "🏆 WORLD FIRST",
      route: "/thermilif"
    },
    {
      id: 4,
      name: "ThermiQ RF",
      category: "Skin Rejuvenation",
      description: "Advanced microneedling RF system combining precise microneedling with radiofrequency energy. Creates microchannels while delivering controlled heat.",
      features: ["Microneedling RF", "Collagen Boost", "Deep Dermis", "Minimal Downtime"],
      icon: faFire,
      color: "#FF8E53",
      image: thermiqRF || laserepi,
      badge: "🔥 POPULAR",
      route: "/rf"
    },
   
    {
      id: 5,
      name: "PICTRON 11",
      category: "Picosecond Laser",
      description: "Advanced picosecond laser with four wavelengths and ultra-short pulses (550-750ps) for tattoos and pigmentation.",
      features: ["4 Wavelengths", "550-750ps Pulses", "Dual-Pulse Tech", "All Skin Types"],
      icon: faGem,
      color: "#9C27B0",
      image: pictron11 || laserepi,
      badge: "💎 ADVANCED",
      route: "/pictron11"
    },
    {
      id: 6,
      name: "AXISONE CO2",
      category: "Fractional CO2",
      description: "Super Pulse CO2 Fractional Laser with RF Metal Tube technology for resurfacing and rejuvenation.",
      features: ["RF Metal Tube", "Super Pulse", "Fractional Mode", "Gynecology"],
      icon: faMicroscope,
      color: "#00ACC1",
      image: axisoneCO2 || laserepi,
      badge: "🔬 MEDICAL GRADE",
      route: "/co2"
    },
    {
      id: 7,
      name: "AI REVEAL",
      category: "Skin Analysis",
      description: "Advanced skin analyzer with 8-spectrum imaging and AI algorithms for 17+ facial skin concerns.",
      features: ["8-Spectrum Imaging", "AI Algorithms", "3D Mapping", "17+ Concerns"],
      icon: faRobot,
      color: "#7C4DFF",
      image: aiReveal || laserepi,
      badge: "🤖 AI-POWERED",
      route: "/aiskin"
    }
  ];
  
  // const handleOpenPDF = () => {
  //   window.open("https://romanqadir.github.io/jsonapi/epilyum%20general.pdf");
  // };
  const handleOpenPDF = () => {
  // Using fetch to get the file as blob
  fetch("https://romanqadir.github.io/jsonapi/epilyum%20general.pdf")
    .then(response => response.blob())
    .then(blob => {
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'general-catalogue.pdf'; // Specify the filename for download
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
    })
    .catch(error => {
      console.error('Download failed:', error);
      // Fallback to opening in new tab if download fails
      window.open("https://romanqadir.github.io/jsonapi/epilyum%20general.pdf", '_blank');
    });
};
  
  return (
    <>
      <HeroContainer id="home">
        {/* Full-width banner with overlay text - removed particles */}
        <HeroBanner>
          <BannerContent>
            <BannerBrand>Epilyum Technology</BannerBrand>
            <BannerTitle>Redefining Beauty Standards</BannerTitle>
            <BannerSubtitle>
              Everything you need for comprehensive aesthetic treatments
            </BannerSubtitle>
            
            <MarqueeContainer>
              <MarqueeContent>
                {[...Array(2)].map((_, i) => ( // Reduced from 3 to 2 for better performance
                  <React.Fragment key={i}>
                    <MarqueeItem><FontAwesomeIcon icon={faCrown} /> FDA Approved</MarqueeItem>
                    <MarqueeItem><FontAwesomeIcon icon={faMedal} /> 10+ Years Excellence</MarqueeItem>
                    <MarqueeItem><FontAwesomeIcon icon={faRocket} /> Advanced Technology</MarqueeItem>
                    <MarqueeItem><FontAwesomeIcon icon={faCircleCheck} /> Dermatologist Approved</MarqueeItem>
                    <MarqueeItem><FontAwesomeIcon icon={faHeart} /> Patient Comfort</MarqueeItem>
                    <MarqueeItem><FontAwesomeIcon icon={faShieldAlt} /> Safety First</MarqueeItem>
                  </React.Fragment>
                ))}
              </MarqueeContent>
            </MarqueeContainer>
          </BannerContent>
        </HeroBanner>

        {/* ALL DEVICES BY EPILYUM COMPANY SECTION */}
        <CatalogueSection>
          <Container>
            <SectionHeader>
              <SectionTag>
                The Epilyum Collection
              </SectionTag>
              <br />
              <SectionTitle>
                The Epilyum  <span>Collective </span>
              </SectionTitle>
              <SectionSubtitle>
                Discover our comprehensive range of advanced aesthetic devices, 
                each engineered with cutting-edge technology to deliver exceptional results
              </SectionSubtitle>
            </SectionHeader>

            {devices.map((device, index) => (
              <DeviceCard key={device.id}>
                <DeviceRow>
                  {index % 2 === 0 ? (
                    <>
                      <DeviceImageCol lg={5} md={12}>
                        <DeviceBadge color={device.color}>
                          <FontAwesomeIcon icon={faCrown} />
                          {device.badge}
                        </DeviceBadge>
                        <DeviceImageWrapper color={device.color}>
                          <DeviceImage src={device.image} alt={device.name} />
                        </DeviceImageWrapper>
                      </DeviceImageCol>
                      
                      <DeviceContentCol lg={7} md={12}>
                        <DeviceCategory color={device.color}>
                          <FontAwesomeIcon icon={device.icon} />
                          {device.category}
                        </DeviceCategory>
                        <DeviceName>{device.name}</DeviceName>
                        <DeviceDescription>{device.description}</DeviceDescription>
                        
                        <FeatureTags>
                          {device.features.map((feature, i) => (
                            <FeatureTag key={i} color={device.color}>
                              <FontAwesomeIcon icon={faCircleCheck} />
                              {feature}
                            </FeatureTag>
                          ))}
                        </FeatureTags>
                      
                        <Link to={device.route} style={{ textDecoration: 'none' }}>
                          <ExploreBtn>
                            Explore {device.name} <FontAwesomeIcon icon={faArrowRight} />
                          </ExploreBtn>
                        </Link>
                      </DeviceContentCol>
                    </>
                  ) : (
                    <>
                      <DeviceContentCol lg={7} md={12}>
                        <DeviceCategory color={device.color}>
                          <FontAwesomeIcon icon={device.icon} />
                          {device.category}
                        </DeviceCategory>
                        <DeviceName>{device.name}</DeviceName>
                        <DeviceDescription>{device.description}</DeviceDescription>
                        
                        <FeatureTags>
                          {device.features.map((feature, i) => (
                            <FeatureTag key={i} color={device.color}>
                              <FontAwesomeIcon icon={faCircleCheck} />
                              {feature}
                            </FeatureTag>
                          ))}
                        </FeatureTags>
                        
                        <Link to={device.route} style={{ textDecoration: 'none' }}>
                          <ExploreBtn>
                            Explore {device.name} <FontAwesomeIcon icon={faArrowRight} />
                          </ExploreBtn>
                        </Link>
                      </DeviceContentCol>
                      
                      <DeviceImageCol lg={5} md={12}>
                        <DeviceBadge color={device.color}>
                          <FontAwesomeIcon icon={faCrown} />
                          {device.badge}
                        </DeviceBadge>
                        <DeviceImageWrapper color={device.color}>
                          <DeviceImage src={device.image} alt={device.name} />
                        </DeviceImageWrapper>
                      </DeviceImageCol>
                    </>
                  )}
                </DeviceRow>
              </DeviceCard>
            ))}
          </Container>
        </CatalogueSection>

        {/* DOWNLOAD FULL CATALOGUE SECTION */}
        <DownloadSection>
          <DownloadContainer>
            <DownloadCard>
              <DownloadTitle>
                Complete <span>Epilyum Catalogue</span>
              </DownloadTitle>
              <DownloadText>
                Download our comprehensive product catalogue featuring detailed specifications, 
                clinical studies, and treatment protocols for all Epilyum devices.
              </DownloadText>
              
              <DownloadBtn onClick={handleOpenPDF}>
                <FontAwesomeIcon icon={faDownload} />
                Download Full Catalogue (PDF)
              </DownloadBtn>
              
              <StatsRow>
                <Col xs={4} md={4}>
                  <StatNumber>7+</StatNumber>
                  <StatLabel>Devices</StatLabel>
                </Col>
                <Col xs={4} md={4}>
                  <StatNumber>20+</StatNumber>
                  <StatLabel>Treatments</StatLabel>
                </Col>
                <Col xs={4} md={4}>
                  <StatNumber>100%</StatNumber>
                  <StatLabel>Results</StatLabel>
                </Col>
              </StatsRow>
            </DownloadCard>
          </DownloadContainer>
        </DownloadSection>
        
        <WhatsappButton
          href={`https://wa.me/${phoneNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Contact us via WhatsApp"
        >
          <FontAwesomeIcon icon={faWhatsapp} />
        </WhatsappButton>
      </HeroContainer>
    </>
  );
}

export default HeroSection;